/**
 * One place for every sign-in / account error message a member can see.
 * Firebase's raw messages ("Firebase: Error (auth/invalid-credential).") are
 * never shown — unknown codes get a generic line plus the code, so a bug
 * report screenshot still tells us what happened.
 */

const MESSAGES: Record<string, string> = {
  'auth/invalid-credential': "That email and password don't match. Check for typos, or reset your password.",
  'auth/wrong-password': "That email and password don't match. Check for typos, or reset your password.",
  'auth/user-not-found': "That email and password don't match. Check for typos, or reset your password.",
  'auth/invalid-email': "That doesn't look like a valid email address.",
  'auth/missing-email': 'Please enter your email address.',
  'auth/missing-password': 'Please enter your password.',
  'auth/user-disabled': 'This account has been disabled. Please contact a Kiwi Church admin.',
  'auth/too-many-requests': 'Too many attempts. Wait a few minutes, or reset your password.',
  'auth/network-request-failed': "Can't reach the server. Check your internet connection and try again.",
  'auth/email-already-in-use': 'An account with this email already exists. Try signing in instead.',
  'auth/weak-password': 'Password must be at least 6 characters.',
  'auth/operation-not-allowed': 'Email sign-in is turned off. Please contact a Kiwi Church admin.',
  'auth/requires-recent-login': 'For security, please sign out and back in, then try again.',
  'auth/internal-error': 'Something went wrong on our side. Please try again.',
}

/** Error codes where offering "reset your password" is the helpful next step. */
export const CREDENTIAL_ERROR_CODES = new Set([
  'auth/invalid-credential',
  'auth/wrong-password',
  'auth/user-not-found',
  'auth/too-many-requests',
])

export function friendlyAuthMessage(code: string | undefined): string {
  if (code && MESSAGES[code]) return MESSAGES[code]
  const suffix = code ? ` (${code.replace(/^auth\//, '')})` : ''
  return `Something went wrong signing in${suffix}. Please try again, or use "Report a problem" once you're in.`
}

/** An Error that remembers its Firebase code, so the UI can offer the right next step. */
export class AuthError extends Error {
  code?: string
  constructor(message: string, code?: string) {
    super(message)
    this.name = 'AuthError'
    this.code = code
  }
}

export function toAuthError(error: unknown): AuthError {
  if (error instanceof AuthError) return error
  const code = typeof error === 'object' && error && 'code' in error ? String((error as { code: unknown }).code) : undefined
  if (code?.startsWith('auth/')) return new AuthError(friendlyAuthMessage(code), code)
  if (error instanceof Error && error.message && !error.message.startsWith('Firebase:')) {
    return new AuthError(error.message, code)
  }
  return new AuthError(friendlyAuthMessage(code), code)
}

// ── Messages carried across a sign-out back to the login screen ──────────
const NOTICE_KEY = 'kc-login-notice'

export function setLoginNotice(message: string): void {
  try { sessionStorage.setItem(NOTICE_KEY, message) } catch { /* private mode */ }
}

export function takeLoginNotice(): string {
  try {
    const msg = sessionStorage.getItem(NOTICE_KEY) || ''
    sessionStorage.removeItem(NOTICE_KEY)
    return msg
  } catch {
    return ''
  }
}
